/**
 * Always-on, bounded, in-memory diagnostic capture for the arena client
 * (`play.legendary-arena.com`), plus the pure builders that turn the captured
 * buffer into a credential-redacted, shareable JSON report.
 *
 * The capture wraps `console.error/warn/info/log/debug` (always calling
 * through to the original method — never suppressing output) and registers
 * `window` `'error'` + `'unhandledrejection'` listeners, appending each event
 * to a ring buffer capped at {@link DIAGNOSTIC_BUFFER_CAP}. When the operator
 * hits a freeze, {@link DiagnosticExportButton} reads the live browser
 * context, builds a {@link DiagnosticReport} via the pure
 * {@link buildDiagnosticReport}, and downloads it.
 *
 * This module is pure browser code: it imports nothing from the engine,
 * registry, pre-planning, server, Postgres, or multiplayer-framework surfaces
 * (the boundary grep in EC-260 enforces this). The capture state is a single
 * module-level singleton per page load; the report has zero network egress and
 * is never persisted.
 *
 * Posture locked by WP-228 / EC-260 / D-22801.
 */

import type { BuildProvenance } from './buildProvenance';
import { buildEffectProvenance, type EffectProvenance } from './effectProvenance';

/**
 * One captured diagnostic event — a console call, an uncaught `window` error,
 * or an unhandled promise rejection.
 */
export interface DiagnosticEntry {
  /** Monotonically increasing, page-lifecycle-scoped; resets to 0 only on reset. */
  sequence: number;
  /** Which capture source produced this entry. */
  kind: 'console' | 'error' | 'unhandledrejection';
  /** The console level for `kind: 'console'`; `null` for error/rejection entries. */
  level: 'log' | 'info' | 'warn' | 'error' | 'debug' | null;
  /** The normalized message text (locked construction per WP-228). */
  message: string;
  /** The associated stack trace when one is available, else `null`. */
  stack: string | null;
  /** Wall-clock capture time in milliseconds. */
  atMs: number;
}

/**
 * The client's live transport state at export click — read from the WP-311
 * `connection` store (boardgame.io framework/transport state, never `G`, never
 * persisted). The store fields plus a derived `timeSinceLastFrameMs` (the
 * capture clock minus `lastFrameAtMs`) — the decisive signal for the
 * "waiting-forever-for-a-server-frame" freeze class — and four recovery counters
 * (WP-429 / D-24250), one per silent auto-recovery path in `bgioClient.ts`, so a
 * freeze report names how many times the client already auto-resynced before the
 * operator hit export.
 */
export interface TransportDiagnostics {
  isConnected: boolean;
  lastStateId: number | null;
  hasEverConnected: boolean;
  lastFrameAtMs: number | null;
  timeSinceLastFrameMs: number | null;
  /** Transport-reconnect resyncs fired (`bgioClient` `onTransportReconnect`). */
  reconnectResyncCount: number;
  /** Move-ack-timeout resyncs fired (`bgioClient` `onWatchdogFire`). */
  moveAckResyncCount: number;
  /** Spectator-staleness resyncs fired (`bgioClient` `onSpectatorWatchdogFire`). */
  spectatorStaleResyncCount: number;
  /** Tab-focus resyncs fired (`bgioClient` `onVisibilityChange`). */
  tabFocusResyncCount: number;
}

/**
 * The impure context an exporter collects from live browser globals and passes
 * into {@link buildDiagnosticReport}. `locationHref` MUST already be redacted
 * by the caller before it reaches the builder.
 */
export interface DiagnosticContext {
  appVersion: string;
  gitSha: string;
  buildTimestamp: string;
  capturedAtIso: string;
  /** Already credential-redacted by the caller. */
  locationHref: string;
  matchId: string | null;
  playerId: string | null;
  userAgent: string;
  viewportWidth: number;
  viewportHeight: number;
  /** The current dropped-entry count, sourced from {@link getDroppedEntryCount}. */
  entryDroppedCount: number;
  // why: typed `unknown`, not the engine UIState type — this is the already-
  // audience-filtered projection the caller supplies from the client store; the
  // builder only serializes it and never inspects it, which keeps this module
  // free of any engine import.
  /**
   * The player's own audience-filtered UIState snapshot the caller read from the
   * client store at export click, or `null` when no match is active. Opaque
   * here: it is serialized into the report, never inspected.
   */
  uiStateSnapshot: unknown;
  // why: the INPUT match-setup composition (scheme / mastermind / groups / heroes
  // + the four supply-pile counts) the caller read back from sessionStorage via
  // matchSetupSession at export click, or `null` when none was persisted (a join
  // the client did not create, or a non-browser context). It pairs the *input*
  // config with the snapshot's *live* pile counts so an under-provisioned supply
  // (the Web-Shooters rescue bug class) is one read instead of a code hunt.
  // Opaque here for the same reason as uiStateSnapshot: serialized, never inspected.
  matchSetup: unknown;
  // why: the client's live transport state at click, assembled by the caller via
  // buildTransportDiagnostics from the connection store + the capture clock. A
  // typed block (unlike the opaque payloads above) so the builder/tests read it.
  transport: TransportDiagnostics;
  // why: which client build, server build and match-creation time produced this
  // match (diagnostics/buildProvenance.ts). Optional: the play root loads it once
  // per match, and it is absent until that load finishes or outside a match.
  buildProvenance?: BuildProvenance | null;
}

/**
 * The serializable report envelope: the scalar context fields plus the derived
 * `entryCount` / `truncated`, the current UIState snapshot, plus the captured
 * `entries`.
 */
export interface DiagnosticReport {
  appVersion: string;
  gitSha: string;
  buildTimestamp: string;
  capturedAtIso: string;
  locationHref: string;
  matchId: string | null;
  playerId: string | null;
  userAgent: string;
  viewportWidth: number;
  viewportHeight: number;
  entryCount: number;
  entryDroppedCount: number;
  truncated: boolean;
  /**
   * The player's own audience-filtered UIState snapshot at export click, or
   * `null` when no match was active. Carried through opaque — see the typing
   * rationale on {@link DiagnosticContext.uiStateSnapshot}.
   */
  uiStateSnapshot: unknown;
  /**
   * The INPUT match-setup composition at export click, or `null` when none was
   * persisted. Carried through opaque — see {@link DiagnosticContext.matchSetup}.
   */
  matchSetup: unknown;
  /**
   * The client's live transport state at export click (WP-428 / D-24249):
   * connection status, last `_stateID`, and frame-staleness. Carried through
   * from {@link DiagnosticContext.transport} unmodified — see
   * {@link buildTransportDiagnostics} for how the caller assembles it.
   */
  transport: TransportDiagnostics;
  /**
   * The client build, the running server build and the match creation time, with a
   * flag when the match was created before the running server booted (so it may be
   * running card rules from an earlier build). Null when not collected.
   */
  buildProvenance: BuildProvenance | null;
  /**
   * Derived card-effect provenance (WP-314 / D-24100): what the turn is blocked on
   * (`awaitingPlayerInput`) and the recently-played cards with an inferred `outcome`,
   * computed from the `uiStateSnapshot` this report already carries. Makes a
   * "froze after I played X" report name its own cause. Always present (empty when no
   * snapshot); never throws.
   */
  effectProvenance: EffectProvenance;
  // why: WP-575 / D-24384 — the engine's per-dispatch effect traces
  // (G.diagnostics.traces, projected onto UIState.effectTraces by WP-488/WP-575).
  // Extracted STRUCTURALLY from the opaque `uiStateSnapshot` this report already
  // carries — NOT via an engine import (EC-260 boundary; the arena-client imports
  // nothing from the engine). Typed `unknown[]` and carried through opaque like
  // `uiStateSnapshot` itself: the builder serializes the records for the export, it
  // never inspects their fields. Always an ARRAY (empty when no snapshot or no
  // traces), never undefined — a report always has an answer for "what did my cards
  // dispatch?" This is the channel the console buffer never was: `entryCount: 0` on
  // a clean match is CORRECT (its call sites are failure paths); the traces are where
  // a normal match's behavioural record actually lives.
  effectTraces: unknown[];
  entries: DiagnosticEntry[];
}

// why: DIAGNOSTIC_BUFFER_CAP bounds the in-memory capture so an always-on
// buffer cannot grow without limit on a long-lived page; on overflow the
// oldest entry is dropped and entryDroppedCount increments so the exported
// report's truncated flag discloses the loss.
export const DIAGNOSTIC_BUFFER_CAP = 200;

/** The console levels this capture wraps. */
type ConsoleLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';
type ConsoleMethod = (...args: unknown[]) => void;

// why: a non-enumerable marker stamped on each replaced console method so a
// Vite HMR module re-instantiation (which resets `installed`) can detect an
// already-wrapped method and not double-wrap it.
const WRAPPED_MARKER = '__diagnosticCaptureWrapped__';

// --- module-singleton capture state (one instance per page load) ---
let captureBuffer: DiagnosticEntry[] = [];
let nextSequence = 0;
let droppedEntryCount = 0;
let installed = false;
let savedConsoleMethods: Partial<Record<ConsoleLevel, ConsoleMethod>> = {};
let windowErrorListener: EventListener | null = null;
let windowRejectionListener: EventListener | null = null;

/**
 * Reads a millisecond wall-clock timestamp for a captured entry.
 *
 * @returns The current time in milliseconds.
 */
function captureTimestampMs(): number {
  // why: Date.now() here is a client-layer diagnostic timestamp marking when an
  // entry was captured; it is outside the engine determinism boundary, which
  // governs packages/game-engine only.
  return Date.now();
}

/**
 * Reports whether a console method already carries the wrapped marker.
 *
 * @param method The console method to inspect.
 * @returns `true` when the method is already one of our wrappers.
 */
function isWrappedConsoleMethod(method: ConsoleMethod): boolean {
  return (method as unknown as Record<string, unknown>)[WRAPPED_MARKER] === true;
}

/**
 * Stamps a non-enumerable wrapped marker on a console method.
 *
 * @param method The wrapper method to mark.
 */
function markConsoleMethodWrapped(method: ConsoleMethod): void {
  Object.defineProperty(method, WRAPPED_MARKER, {
    value: true,
    enumerable: false,
    configurable: true,
    writable: false,
  });
}

/**
 * Appends an entry to the ring buffer, assigning the next sequence number and
 * enforcing the buffer cap.
 *
 * @param partial The entry fields other than `sequence`.
 */
function appendEntry(partial: Omit<DiagnosticEntry, 'sequence'>): void {
  const entry: DiagnosticEntry = {
    sequence: nextSequence,
    kind: partial.kind,
    level: partial.level,
    message: partial.message,
    stack: partial.stack,
    atMs: partial.atMs,
  };
  nextSequence += 1;
  captureBuffer.push(entry);
  // why: on overflow drop the oldest entry and increment droppedEntryCount so
  // the report's truncated flag (= droppedEntryCount > 0) discloses the loss.
  if (captureBuffer.length > DIAGNOSTIC_BUFFER_CAP) {
    captureBuffer.shift();
    droppedEntryCount += 1;
  }
}

/**
 * Derives the `message` and `stack` for a wrapped `console.*` call per the
 * locked construction: a non-`Error` argument list is `String(value)`-joined
 * by a single space; if any argument is an `Error`, the first such error's
 * `.message` and `.stack` win.
 *
 * @param args The arguments passed to the console method.
 * @returns The normalized message and stack.
 */
function buildConsoleMessageAndStack(args: unknown[]): {
  message: string;
  stack: string | null;
} {
  let firstError: Error | null = null;
  for (const arg of args) {
    if (arg instanceof Error) {
      firstError = arg;
      break;
    }
  }
  if (firstError !== null) {
    return { message: firstError.message, stack: firstError.stack ?? null };
  }
  const stringifiedArgs: string[] = [];
  for (const arg of args) {
    stringifiedArgs.push(String(arg));
  }
  return { message: stringifiedArgs.join(' '), stack: null };
}

/**
 * Builds a wrapper for a console method that records the call and then always
 * calls through to the original.
 *
 * @param level    The console level being wrapped.
 * @param original The original console method to call through to.
 * @returns The wrapper method.
 */
function makeWrappedConsoleMethod(
  level: ConsoleLevel,
  original: ConsoleMethod,
): ConsoleMethod {
  return function wrappedConsoleMethod(...args: unknown[]): void {
    // why: call through to the original console method first and always, so the
    // capture never suppresses output (pass-through capture).
    original.apply(console, args);
    const { message, stack } = buildConsoleMessageAndStack(args);
    appendEntry({
      kind: 'console',
      level,
      message,
      stack,
      atMs: captureTimestampMs(),
    });
  };
}

/**
 * Returns a wrapped replacement for one console method, saving the original
 * first. A method that already carries the wrapped marker (HMR re-instantiation
 * reset `installed` but left the live wrapper in place) is returned unchanged so
 * it is never double-wrapped.
 *
 * @param level   The console level being wrapped.
 * @param current The live console method for that level.
 * @returns The method to assign back to `console[level]`.
 */
function wrapConsoleMethodIfNeeded(
  level: ConsoleLevel,
  current: ConsoleMethod,
): ConsoleMethod {
  if (isWrappedConsoleMethod(current)) {
    return current;
  }
  savedConsoleMethods[level] = current;
  const wrapped = makeWrappedConsoleMethod(level, current);
  markConsoleMethodWrapped(wrapped);
  return wrapped;
}

/**
 * Wraps every console level once, saving the originals. Written as explicit
 * per-level reassignments (not a loop) so each `console.<level>` wrap is direct
 * and obvious.
 */
function installConsoleWraps(): void {
  console.log = wrapConsoleMethodIfNeeded('log', console.log);
  console.info = wrapConsoleMethodIfNeeded('info', console.info);
  console.warn = wrapConsoleMethodIfNeeded('warn', console.warn);
  console.error = wrapConsoleMethodIfNeeded('error', console.error);
  console.debug = wrapConsoleMethodIfNeeded('debug', console.debug);
}

/**
 * Normalizes a `window` `'error'` event: prefer `event.error` (an `Error`) for
 * `message` + `stack`, else fall back to `event.message` with no stack.
 *
 * @param event The error event.
 * @returns The normalized message and stack.
 */
function normalizeWindowErrorEvent(event: ErrorEvent): {
  message: string;
  stack: string | null;
} {
  if (event.error instanceof Error) {
    return { message: event.error.message, stack: event.error.stack ?? null };
  }
  return { message: event.message, stack: null };
}

/**
 * Normalizes an `'unhandledrejection'` event: when the reason is an `Error`,
 * use its `.message` + `.stack`; otherwise `String(reason)` with no stack.
 *
 * @param event The promise-rejection event.
 * @returns The normalized message and stack.
 */
function normalizeRejectionEvent(event: PromiseRejectionEvent): {
  message: string;
  stack: string | null;
} {
  const reason: unknown = event.reason;
  if (reason instanceof Error) {
    return { message: reason.message, stack: reason.stack ?? null };
  }
  return { message: String(reason), stack: null };
}

/**
 * Registers the `window` `'error'` + `'unhandledrejection'` listeners and saves
 * their references so reset can remove them.
 */
function installWindowListeners(): void {
  windowErrorListener = (event: Event): void => {
    const { message, stack } = normalizeWindowErrorEvent(event as ErrorEvent);
    appendEntry({
      kind: 'error',
      level: null,
      message,
      stack,
      atMs: captureTimestampMs(),
    });
  };
  windowRejectionListener = (event: Event): void => {
    const { message, stack } = normalizeRejectionEvent(
      event as PromiseRejectionEvent,
    );
    appendEntry({
      kind: 'unhandledrejection',
      level: null,
      message,
      stack,
      atMs: captureTimestampMs(),
    });
  };
  window.addEventListener('error', windowErrorListener);
  window.addEventListener('unhandledrejection', windowRejectionListener);
}

/**
 * Installs the diagnostic capture: wraps the console methods and registers the
 * window listeners. Idempotent — a second call NO-OPs via the module-level
 * `installed` flag, and the per-method wrapped marker additionally guards
 * against double-wrapping under a Vite HMR module re-instantiation.
 */
export function installDiagnosticCapture(): void {
  if (installed) {
    return;
  }
  installed = true;
  installConsoleWraps();
  installWindowListeners();
}

/**
 * Returns the captured entries oldest → newest (by ascending `sequence`) as a
 * defensive copy.
 *
 * @returns The current buffer contents, oldest first.
 */
export function getDiagnosticEntries(): DiagnosticEntry[] {
  return captureBuffer.slice();
}

/**
 * Returns the number of entries dropped from the front of the buffer because of
 * the {@link DIAGNOSTIC_BUFFER_CAP} overflow.
 *
 * @returns The dropped-entry count.
 */
export function getDroppedEntryCount(): number {
  return droppedEntryCount;
}

/**
 * Tears the capture back down for test isolation: restores the original console
 * methods, removes the window listeners, clears the buffer, and resets the
 * sequence + dropped counters. Production code never calls this.
 */
export function resetDiagnosticCaptureForTesting(): void {
  if (savedConsoleMethods.log !== undefined) {
    console.log = savedConsoleMethods.log;
  }
  if (savedConsoleMethods.info !== undefined) {
    console.info = savedConsoleMethods.info;
  }
  if (savedConsoleMethods.warn !== undefined) {
    console.warn = savedConsoleMethods.warn;
  }
  if (savedConsoleMethods.error !== undefined) {
    console.error = savedConsoleMethods.error;
  }
  if (savedConsoleMethods.debug !== undefined) {
    console.debug = savedConsoleMethods.debug;
  }
  savedConsoleMethods = {};
  if (windowErrorListener !== null) {
    window.removeEventListener('error', windowErrorListener);
    windowErrorListener = null;
  }
  if (windowRejectionListener !== null) {
    window.removeEventListener('unhandledrejection', windowRejectionListener);
    windowRejectionListener = null;
  }
  captureBuffer = [];
  nextSequence = 0;
  droppedEntryCount = 0;
  installed = false;
}

/**
 * Redacts the `credentials` query-param value in a URL, replacing it with the
 * literal `***redacted***` while leaving `match` / `player` intact.
 *
 * @param href The (typically absolute) location href to redact.
 * @returns The href with the credentials value redacted.
 */
export function redactCredentialsFromUrl(href: string): string {
  // why: redaction targets the credentials query param only (the live-match
  // session secret); match/player are retained for correlation in the report.
  try {
    const url = new URL(href);
    if (url.searchParams.has('credentials')) {
      url.searchParams.set('credentials', '***redacted***');
    }
    return url.toString();
  } catch (urlParseError) {
    // why: a non-absolute or malformed href cannot be parsed by URL(); a
    // string-level fallback still strips the credentials value so the session
    // secret never survives into the report even for an unparseable href.
    return href.replace(/([?&]credentials=)[^&#]*/g, '$1***redacted***');
  }
}

/**
 * Builds the report envelope from the captured entries and the collected
 * context. Pure: it performs no ambient `window` / `Date` / global reads — the
 * impure caller passes everything in.
 *
 * @param entries The captured entries, oldest → newest.
 * @param context The browser context collected by the caller.
 * @returns The assembled report.
 */
/**
 * Assembles the typed transport-diagnostics block from the `connection` store's
 * fields and the single click-time capture clock. Pure and clock-free: the
 * caller passes `capturedAtMs` (the impure exporter already read it), so this
 * — like {@link buildDiagnosticReport} — reads no ambient `Date`/`window`.
 *
 * @param state       The connection-store fields (structural, no store import).
 * @param capturedAtMs The single click-time `Date.now()` read.
 * @returns The transport block, with `timeSinceLastFrameMs` derived (or `null`).
 */
export function buildTransportDiagnostics(
  state: {
    isConnected: boolean;
    lastStateId: number | null;
    hasEverConnected: boolean;
    lastFrameAtMs: number | null;
    reconnectResyncCount: number;
    moveAckResyncCount: number;
    spectatorStaleResyncCount: number;
    tabFocusResyncCount: number;
  },
  capturedAtMs: number,
): TransportDiagnostics {
  const timeSinceLastFrameMs =
    state.lastFrameAtMs === null ? null : capturedAtMs - state.lastFrameAtMs;
  return {
    isConnected: state.isConnected,
    lastStateId: state.lastStateId,
    hasEverConnected: state.hasEverConnected,
    lastFrameAtMs: state.lastFrameAtMs,
    timeSinceLastFrameMs,
    // why: the four recovery counters (WP-429 / D-24250) are copied from the
    // connection store verbatim — no derivation. Each is incremented in
    // bgioClient.ts at the moment its recovery path commits to a resync.
    reconnectResyncCount: state.reconnectResyncCount,
    moveAckResyncCount: state.moveAckResyncCount,
    spectatorStaleResyncCount: state.spectatorStaleResyncCount,
    tabFocusResyncCount: state.tabFocusResyncCount,
  };
}

/**
 * Extracts the engine's per-dispatch effect traces from the opaque
 * `uiStateSnapshot` (WP-575). Read STRUCTURALLY with runtime guards — no engine
 * import, no type assertion into the engine `UIState`/`EffectTrace` (EC-260
 * boundary). The projected field is `UIState.effectTraces` (WP-488 / D-24384);
 * when the snapshot is null, not an object, or carries no `effectTraces` array,
 * the result is an empty array so the report always carries a defined array.
 *
 * @param uiStateSnapshot The opaque audience-filtered snapshot the caller supplied.
 * @returns The effect-trace records as an array, empty when none are present.
 */
function extractEffectTraces(uiStateSnapshot: unknown): unknown[] {
  if (uiStateSnapshot === null || typeof uiStateSnapshot !== 'object') {
    return [];
  }
  const candidate = (uiStateSnapshot as { effectTraces?: unknown }).effectTraces;
  if (!Array.isArray(candidate)) {
    return [];
  }
  // why: return a shallow copy so the report does not alias the snapshot's own
  // array; the records themselves are carried through opaque (serialized, never
  // inspected here), matching the module's uiStateSnapshot pass-through posture.
  return candidate.slice();
}

export function buildDiagnosticReport(
  entries: DiagnosticEntry[],
  context: DiagnosticContext,
): DiagnosticReport {
  return {
    appVersion: context.appVersion,
    gitSha: context.gitSha,
    buildTimestamp: context.buildTimestamp,
    capturedAtIso: context.capturedAtIso,
    locationHref: context.locationHref,
    matchId: context.matchId,
    playerId: context.playerId,
    userAgent: context.userAgent,
    viewportWidth: context.viewportWidth,
    viewportHeight: context.viewportHeight,
    entryCount: entries.length,
    entryDroppedCount: context.entryDroppedCount,
    truncated: context.entryDroppedCount > 0,
    uiStateSnapshot: context.uiStateSnapshot,
    matchSetup: context.matchSetup,
    // why: pass the caller-assembled transport block straight through (like
    // uiStateSnapshot / matchSetup) so the builder stays pure — the one clock
    // subtraction already happened in buildTransportDiagnostics at collect time.
    transport: context.transport,
    buildProvenance: context.buildProvenance ?? null,
    // why: derive provenance from the snapshot the caller already collected — no new
    // context field, so the impure exporter (DiagnosticExportButton) is unchanged. No
    // resolver is passed: the arena-client has no client-side card-text source, so
    // abilityText degrades to null (fail-soft) per D-24100.
    effectProvenance: buildEffectProvenance(context.uiStateSnapshot),
    // why: WP-575 — surface the engine's per-dispatch effect traces from the same
    // snapshot the report already carries (no new context field, so the impure
    // exporter is unchanged). Structural read via extractEffectTraces — no engine
    // import (EC-260). Always an array so the report never carries `undefined`.
    effectTraces: extractEffectTraces(context.uiStateSnapshot),
    entries,
  };
}

/**
 * Serializes a report to 2-space-indented JSON suitable for download and
 * clipboard sharing.
 *
 * @param report The report to serialize.
 * @returns The pretty-printed JSON string.
 */
export function serializeDiagnosticReport(report: DiagnosticReport): string {
  return JSON.stringify(report, null, 2);
}

/**
 * Builds the download file name for a diagnostic report. An absent match id
 * becomes the literal `no-match`; `/` and `\` in the id are replaced with `-`
 * so the name stays a single safe filename component.
 *
 * @param matchId      The match id (already URL-decoded) or `null` when absent.
 * @param capturedAtMs The single `Date.now()` read at click time.
 * @returns The `.json` file name.
 */
export function buildDiagnosticFileName(
  matchId: string | null,
  capturedAtMs: number,
): string {
  const rawMatchSegment = matchId !== null && matchId !== '' ? matchId : 'no-match';
  const safeMatchSegment = rawMatchSegment.replaceAll('/', '-').replaceAll('\\', '-');
  return `legendary-arena-diagnostics-${safeMatchSegment}-${capturedAtMs}.json`;
}
